import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/premio-imagen`;

const EMPTY = {
  nombre: '',
  descripcion: '',
  costo: '',
  rutaURL: '',
  fechaIniVigencia: '',
  fechaFinVigencia: '',
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const { getSystemConfig } = useLogos();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState(EMPTY);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previousImagePath, setPreviousImagePath] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, rutaURL: 'Solo se permiten archivos de imagen.' }));
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrors(prev => ({ ...prev, rutaURL: `La imagen no debe superar los ${maxMB}MB.` }));
      return;
    }

    setSelectedFile(file);

    const blobUrl = URL.createObjectURL(file);
    setForm(prev => ({ ...prev, rutaURLPreview: blobUrl }));

    try {
      setIsUploading(true);
      if (previousImagePath) {
        try {
          await axios.delete(UPLOAD_ENDPOINT, {
            params: { filePath: previousImagePath },
            withCredentials: true
          });
        } catch (deleteError) {
          console.warn('No se pudo eliminar la imagen anterior:', deleteError);
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      const resUpload = await axios.post(UPLOAD_ENDPOINT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const resData = resUpload.data;
      setPreviousImagePath(resData.filePath);

      setForm(prev => ({
        ...prev,
        rutaURL: resData.filePath,
        rutaURLPreview: blobUrl,
      }));

      setErrors(prev => ({ ...prev, rutaURL: '' }));
    } catch (error) {
      console.error('Error al subir la imagen del premio:', error);
      setErrors(prev => ({
        ...prev,
        rutaURL: error.message || 'Error al subir la imagen al servidor.',
      }));

      setForm(prev => ({ ...prev, rutaURLPreview: null }));
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const getTodayStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateField = (field, value, currentForm) => {
    let error = '';
    if (!value) {
      error = 'Este campo es obligatorio.';
      return error;
    }

    if (field === 'fechaIniVigencia' || field === 'fechaFinVigencia') {
      const todayStr = getTodayStr();
      const valStr = value.substring(0, 10);

      if (valStr < todayStr) {
        error = 'La fecha no puede ser anterior a hoy.';
      }

      if (field === 'fechaFinVigencia' && currentForm.fechaIniVigencia) {
        const iniStr = currentForm.fechaIniVigencia.substring(0, 10);
        if (valStr < iniStr) {
          error = 'La fecha de fin no puede ser anterior a la de inicio.';
        }
      }
    }
    return error;
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;
    Object.keys(EMPTY).forEach(key => {
      const error = validateField(key, form[key], form);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field, value) => {
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);

    const error = validateField(field, value, updatedForm);
    let nextErrors = { [field]: error };

    if (field === 'fechaIniVigencia' && updatedForm.fechaFinVigencia) {
      nextErrors.fechaFinVigencia = validateField('fechaFinVigencia', updatedForm.fechaFinVigencia, updatedForm);
    }

    setErrors(prev => ({ ...prev, ...nextErrors }));
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filledForm = {
        idPremio: data.idPremio ?? 0,
        nombre: data.nombre ?? '',
        descripcion: data.descripcion ?? '',
        costo: data.costo ?? '',
        rutaURL: data.rutaURL ?? '',
        fechaIniVigencia: data.fechaIniVigencia ?? '',
        fechaFinVigencia: data.fechaFinVigencia ?? '',
      };
      setForm(filledForm);
      if (filledForm.rutaURL) {
        setPreviousImagePath(filledForm.rutaURL);
      }
      Object.keys(filledForm).forEach(key => {
        if (key in EMPTY) {
          const err = validateField(key, filledForm[key], filledForm);
          setErrors(prev => ({ ...prev, [key]: err }));
        }
      });
    } else {
      setForm(EMPTY);
      setErrors(EMPTY);
      setSelectedFile(null);
      setPreviousImagePath(null);
    }
  }, [mode, data]);

  if (!open) return null;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo premio' : 'Editar premio'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
          value={form.nombre}
          onChange={(e) => handleChange('nombre', e.target.value)}
          maxLength={150}
        />
        {errors.nombre && <small style={errorStyle}>{errors.nombre}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Costo (monedas): <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.costo ? ' infoField--error' : ''}`}
          type="number"
          min={0}
          step="1"
          value={form.costo}
          onChange={(e) => handleChange('costo', e.target.value)}
        />
        {errors.costo && <small style={errorStyle}>{errors.costo}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.descripcion ? ' infoField--error' : ''}`}
          value={form.descripcion}
          onChange={(e) => handleChange('descripcion', e.target.value)}
          maxLength={400}
        />
        {errors.descripcion && <small style={errorStyle}>{errors.descripcion}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Inicio vigencia: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.fechaIniVigencia ? ' infoField--error' : ''}`}
          onKeyDown={(e) => e.preventDefault()}
          type="date"
          min={getTodayStr()}
          value={form.fechaIniVigencia ? String(form.fechaIniVigencia).substring(0, 10) : ''}
          onChange={(e) => handleChange('fechaIniVigencia', e.target.value)}
        />
        {errors.fechaIniVigencia && <small style={errorStyle}>{errors.fechaIniVigencia}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fin vigencia: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.fechaFinVigencia ? ' infoField--error' : ''}`}
          onKeyDown={(e) => e.preventDefault()}
          type="date"
          min={form.fechaIniVigencia ? String(form.fechaIniVigencia).substring(0, 10) : getTodayStr()}
          value={form.fechaFinVigencia ? String(form.fechaFinVigencia).substring(0, 10) : ''}
          onChange={(e) => handleChange('fechaFinVigencia', e.target.value)}
        />
        {errors.fechaFinVigencia && <small style={errorStyle}>{errors.fechaFinVigencia}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Imagen del premio: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {/* Área de carga con preview integrado */}
          <div
            className={`file-uploader${form.rutaURL ? ' file-uploader--filled' : ''}${errors.rutaURL ? ' file-uploader--error' : ''}${isUploading ? ' file-uploader--uploading' : ''}`}
          >
            {form.rutaURL && (
              <div className="file-uploader__badge">
                <span>✓</span>
                <span>Archivo cargado</span>
              </div>
            )}

            {form.rutaURL && (
              <div className="file-uploader__preview">
                <img
                  src={form.rutaURLPreview || (form.rutaURL?.startsWith('http') ? form.rutaURL : `${SERVER_URL}${form.rutaURL}`)}
                  alt="Vista previa del premio"
                />
              </div>
            )}

            <label className="file-uploader__label">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                onClick={(e) => { e.target.value = ''; }}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
              <div className="file-uploader__icon">
                {isUploading ? '⏳' : (form.rutaURL ? '🔄' : '☁️')}
              </div>
              <div className="file-uploader__text">
                {isUploading ? 'Subiendo archivo...' : (form.rutaURL ? 'Cambiar imagen' : 'Haz clic para subir imagen')}
              </div>
              <div className="file-uploader__hint">
                JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
              </div>
            </label>
          </div>

          {errors.rutaURL && <small style={errorStyle}>{errors.rutaURL}</small>}
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) {
            onSubmit(form, mode);
          }
        }}
        disabled={isUploading || Object.values(form).some(x => x === '' || x === null) || Object.values(errors).some(x => x)}
        style={{
          opacity: isUploading || Object.values(form).some(x => x === '' || x === null) || Object.values(errors).some(x => x) ? 0.5 : 1,
          cursor: isUploading || Object.values(form).some(x => x === '' || x === null) || Object.values(errors).some(x => x) ? 'not-allowed' : 'pointer',
        }}
      >
        {isUploading ? 'Subiendo...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
